import React, { useState, useEffect } from 'react';

class CapTableCalculator {
  constructor() {
    this.data = this.getDefaultData();
  }

  getDefaultData() {
    return {
      founders: [
        { name: 'Founder 1', shares: 0, percentage: 0 },
        { name: 'Founder 2', shares: 0, percentage: 0 },
        { name: 'Founder 3', shares: 0, percentage: 0 }
      ],
      employeePool: { shares: 0, percentage: 0 },
      investors: [
        { name: 'Investor 1', shares: 0, percentage: 0 }
      ]
    };
  }

  calculateTotals() {
    const founderTotal = this.data.founders.reduce((sum, founder) => sum + (founder.shares || 0), 0);
    const employeeTotal = this.data.employeePool.shares || 0;
    const investorTotal = this.data.investors.reduce((sum, investor) => sum + (investor.shares || 0), 0);
    const grandTotal = founderTotal + employeeTotal + investorTotal;

    return {
      founders: founderTotal,
      employees: employeeTotal,
      investors: investorTotal,
      grandTotal
    };
  }

  calculatePercentages() {
    const totals = this.calculateTotals();
    const { grandTotal } = totals;

    if (grandTotal === 0) return this.data;

    // Update founder percentages
    this.data.founders.forEach(founder => {
      founder.percentage = grandTotal > 0 ? (founder.shares / grandTotal) * 100 : 0;
    });

    // Update employee pool percentage
    this.data.employeePool.percentage = grandTotal > 0 ? (this.data.employeePool.shares / grandTotal) * 100 : 0;

    // Update investor percentages
    this.data.investors.forEach(investor => {
      investor.percentage = grandTotal > 0 ? (investor.shares / grandTotal) * 100 : 0;
    });

    return this.data;
  }

  updateFounderShares(index, shares) {
    // Remove commas and parse to integer
    const cleanShares = String(shares).replace(/,/g, '');
    this.data.founders[index].shares = parseInt(cleanShares) || 0;
    return this.calculatePercentages();
  }

  updateEmployeeShares(shares) {
    // Remove commas and parse to integer
    const cleanShares = String(shares).replace(/,/g, '');
    this.data.employeePool.shares = parseInt(cleanShares) || 0;
    return this.calculatePercentages();
  }

  updateInvestorShares(index, shares) {
    // Remove commas and parse to integer
    const cleanShares = String(shares).replace(/,/g, '');
    this.data.investors[index].shares = parseInt(cleanShares) || 0;
    return this.calculatePercentages();
  }

  addFounder() {
    this.data.founders.push({ 
      name: `Founder ${this.data.founders.length + 1}`, 
      shares: 0, 
      percentage: 0 
    });
    return this.calculatePercentages();
  }

  removeFounder(index) {
    if (this.data.founders.length > 1) {
      this.data.founders.splice(index, 1);
      return this.calculatePercentages();
    }
    return this.data;
  }

  addInvestor() {
    this.data.investors.push({ 
      name: `Investor ${this.data.investors.length + 1}`, 
      shares: 0, 
      percentage: 0 
    });
    return this.calculatePercentages();
  }

  removeInvestor(index) {
    if (this.data.investors.length > 1) {
      this.data.investors.splice(index, 1);
      return this.calculatePercentages();
    }
    return this.data;
  }

  getData() {
    return this.data;
  }

  setData(newData) {
    // Migrate old data structure if needed
    if (newData.seedInvestors || newData.seriesAInvestors) {
      const migratedData = { ...newData };
      migratedData.investors = [
        ...(newData.seedInvestors || []),
        ...(newData.seriesAInvestors || [])
      ];
      delete migratedData.seedInvestors;
      delete migratedData.seriesAInvestors;
      this.data = migratedData;
    } else {
      this.data = newData;
    }
    return this.calculatePercentages();
  }
}

const CapTableExcel = ({ onChange, initialData = null }) => {
  const [calculator] = useState(new CapTableCalculator());
  const [data, setData] = useState(calculator.getData());
  const [totals, setTotals] = useState(calculator.calculateTotals());

  useEffect(() => {
    if (initialData) {
      const newData = calculator.setData(initialData);
      setData(newData);
      setTotals(calculator.calculateTotals());
    }
  }, [initialData, calculator]);

  const handleDataChange = (newData) => {
    setData(newData);
    setTotals(calculator.calculateTotals());
    if (onChange) {
      onChange(newData, calculator.calculateTotals());
    }
  };

  const formatNumber = (num) => {
    if (num === 0) return '0';
    return num.toLocaleString();
  };

  const formatPercentage = (percentage) => {
    return `${percentage.toFixed(2)}%`;
  };

  const cellStyle = "px-3 py-2 border border-gray-300 text-sm";
  const headerStyle = "px-3 py-2 bg-gray-100 border border-gray-300 text-sm font-medium text-gray-700";
  const inputStyle = "w-full px-2 py-1 border-0 bg-transparent text-sm focus:outline-none focus:ring-1 focus:ring-blue-500";
  const totalRowStyle = "px-3 py-2 bg-gray-50 border border-gray-300 text-sm font-medium";

  return (
    <div className="cap-table-excel">
      <div className="mb-4">
        <h3 className="text-lg font-medium text-gray-900 mb-2">Cap Table Breakdown</h3>
        <p className="text-sm text-gray-600">
          Please provide your current equity breakdown. All percentages will be calculated automatically.
        </p>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full border-collapse border border-gray-300">
          {/* Header */}
          <thead>
            <tr>
              <th className={headerStyle}>Initial Shareholders</th>
              <th className={headerStyle}>Initial Shares</th>
              <th className={headerStyle}>Fully Diluted Ownership %</th>
              <th className={headerStyle}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {/* Founders */}
            {data.founders.map((founder, index) => (
              <tr key={`founder-${index}`}>
                <td className={cellStyle}>
                  <input
                    type="text"
                    value={founder.name}
                    onChange={(e) => {
                      const newData = { ...data };
                      newData.founders[index].name = e.target.value;
                      handleDataChange(newData);
                    }}
                    className={inputStyle}
                    placeholder="Founder Name"
                  />
                </td>
                <td className={cellStyle}>
                  <input
                    type="text"
                    value={founder.shares || ''}
                    onChange={(e) => {
                      // Allow only numbers and prevent commas
                      const value = e.target.value.replace(/[^0-9]/g, '');
                      const newData = calculator.updateFounderShares(index, value);
                      handleDataChange(newData);
                    }}
                    className={inputStyle}
                    placeholder="0"
                  />
                </td>
                <td className={cellStyle}>
                  {formatPercentage(founder.percentage)}
                </td>
                <td className={cellStyle}>
                  {data.founders.length > 1 && (
                    <button
                      type="button"
                      onClick={() => {
                        const newData = calculator.removeFounder(index);
                        handleDataChange(newData);
                      }}
                      className="text-red-600 hover:text-red-800 text-sm"
                    >
                      Remove
                    </button>
                  )}
                </td>
              </tr>
            ))}
            
            {/* Add Founder Button Row */}
            <tr>
              <td colSpan="4" className={cellStyle}>
                <button
                  type="button"
                  onClick={() => {
                    const newData = calculator.addFounder();
                    handleDataChange(newData);
                  }}
                  className="text-blue-600 hover:text-blue-800 text-sm"
                >
                  + Add Founder
                </button>
              </td>
            </tr>

            {/* Founders Total */}
            <tr>
              <td className={totalRowStyle}>Total</td>
              <td className={totalRowStyle}>{formatNumber(totals.founders)}</td>
              <td className={totalRowStyle}>{formatPercentage(totals.grandTotal > 0 ? (totals.founders / totals.grandTotal) * 100 : 0)}</td>
              <td className={totalRowStyle}></td>
            </tr>

            {/* Empty Row */}
            <tr>
              <td colSpan="4" className="px-3 py-1 border-0"></td>
            </tr>

            {/* Employee Options Pool */}
            <tr>
              <td className={cellStyle}>Employee Options Pool</td>
              <td className={cellStyle}>
                <input
                  type="text"
                  value={data.employeePool.shares || ''}
                  onChange={(e) => {
                    // Allow only numbers and prevent commas
                    const value = e.target.value.replace(/[^0-9]/g, '');
                    const newData = calculator.updateEmployeeShares(value);
                    handleDataChange(newData);
                  }}
                  className={inputStyle}
                  placeholder="0"
                />
              </td>
              <td className={cellStyle}>
                {formatPercentage(data.employeePool.percentage)}
              </td>
              <td className={cellStyle}></td>
            </tr>

            {/* Empty Row */}
            <tr>
              <td colSpan="4" className="px-3 py-1 border-0"></td>
            </tr>

            {/* Investors Header */}
            <tr>
              <td colSpan="4" className="px-3 py-2 bg-blue-50 border border-gray-300 text-sm font-medium text-blue-700">
                Investors
              </td>
            </tr>

            {/* Investors */}
            {data.investors.map((investor, index) => (
              <tr key={`investor-${index}`}>
                <td className={cellStyle}>
                  <input
                    type="text"
                    value={investor.name}
                    onChange={(e) => {
                      const newData = { ...data };
                      newData.investors[index].name = e.target.value;
                      handleDataChange(newData);
                    }}
                    className={inputStyle}
                    placeholder="Investor Name"
                  />
                </td>
                <td className={cellStyle}>
                  <input
                    type="text"
                    value={investor.shares || ''}
                    onChange={(e) => {
                      // Allow only numbers and prevent commas
                      const value = e.target.value.replace(/[^0-9]/g, '');
                      const newData = calculator.updateInvestorShares(index, value);
                      handleDataChange(newData);
                    }}
                    className={inputStyle}
                    placeholder="0"
                  />
                </td>
                <td className={cellStyle}>
                  {formatPercentage(investor.percentage)}
                </td>
                <td className={cellStyle}>
                  {data.investors.length > 1 && (
                    <button
                      type="button"
                      onClick={() => {
                        const newData = calculator.removeInvestor(index);
                        handleDataChange(newData);
                      }}
                      className="text-red-600 hover:text-red-800 text-sm"
                    >
                      Remove
                    </button>
                  )}
                </td>
              </tr>
            ))}

            {/* Add Investor Button */}
            <tr>
              <td colSpan="4" className={cellStyle}>
                <button
                  type="button"
                  onClick={() => {
                    const newData = calculator.addInvestor();
                    handleDataChange(newData);
                  }}
                  className="text-blue-600 hover:text-blue-800 text-sm"
                >
                  + Add Investor
                </button>
              </td>
            </tr>

            {/* Investors Total */}
            <tr>
              <td className={totalRowStyle}>Total</td>
              <td className={totalRowStyle}>{formatNumber(totals.investors)}</td>
              <td className={totalRowStyle}>{formatPercentage(totals.grandTotal > 0 ? (totals.investors / totals.grandTotal) * 100 : 0)}</td>
              <td className={totalRowStyle}></td>
            </tr>

            {/* Empty Row */}
            <tr>
              <td colSpan="4" className="px-3 py-1 border-0"></td>
            </tr>

            {/* Grand Total */}
            <tr>
              <td className="px-3 py-2 bg-blue-100 border border-gray-300 text-sm font-bold text-blue-900">Grand Total</td>
              <td className="px-3 py-2 bg-blue-100 border border-gray-300 text-sm font-bold text-blue-900">{formatNumber(totals.grandTotal)}</td>
              <td className="px-3 py-2 bg-blue-100 border border-gray-300 text-sm font-bold text-blue-900">100.00%</td>
              <td className="px-3 py-2 bg-blue-100 border border-gray-300 text-sm font-bold text-blue-900"></td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Validation Messages */}
      {totals.grandTotal > 0 && (
        <div className="mt-4 p-3 bg-gray-50 rounded-md">
          <h4 className="text-sm font-medium text-gray-900 mb-2">Cap Table Summary:</h4>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-600">Total Founder Ownership:</span>
              <span className="ml-2 font-medium">{formatPercentage((totals.founders / totals.grandTotal) * 100)}</span>
            </div>
            <div>
              <span className="text-gray-600">Total Investor Ownership:</span>
              <span className="ml-2 font-medium">{formatPercentage((totals.investors / totals.grandTotal) * 100)}</span>
            </div>
            <div>
              <span className="text-gray-600">Employee Pool:</span>
              <span className="ml-2 font-medium">{formatPercentage((totals.employees / totals.grandTotal) * 100)}</span>
            </div>
            <div>
              <span className="text-gray-600">Total Shares:</span>
              <span className="ml-2 font-medium">{formatNumber(totals.grandTotal)}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CapTableExcel;